// The permission brain. Pure functions, no I/O, no Express — so the whole matrix in §8 of
// BUILD_SPEC.md can be tested exhaustively without a database, and so BOTH SIDES import the
// same rules. The server calling them is the gate; the frontend calling them is what greys
// out a control before anyone clicks it.
//
// It lives in shared/ rather than in server/ for exactly that reason. There used to be a
// hand-written copy at frontend/src/lib/permissions.js, and it had already drifted: it never
// learned about canNotifyStaff, canManageCatTeams, or the admin_can_manage_teams delegation
// flag, so the UI could not grey those and fell back to letting the server 403 the click.
// Two copies of an authorization rule is one copy plus a latent bug.
//
// No imports, deliberately. Adding one — a database handle, an env read, anything that can
// throw at load — would make the gate a thing that can fail to start.
//
// The shapes these take:
//   actor  = { id, roles[], assistsPmIds[], coordinatesTeamIds[] }
//            The server builds this in auth/provision.js; the browser gets the same fields
//            from GET /api/me, so one shape covers both.
//   ctx    = { mode, teamStaffIds:Set, catSettings, tenant }
//   target = an assignment, in EITHER shape — see assignmentFields below.

export const has = (actor, role) => Boolean(actor?.roles?.includes(role))

export const isOwner = (actor) => has(actor, 'owner')

/**
 * Which admin role governs which board.
 *
 * Two vocabularies meet here, on purpose. The MODE identifiers stay 'normal' and 'cat' —
 * they are written into every assignment and mode period and are never renamed. The ROLE
 * names use the product's Team/Group words, because a role is a thing people read and grant
 * to each other. This object is the only place the two are tied together; a tenant renaming
 * its modes changes captions and nothing else.
 */
export const MODE_ADMIN_ROLE = { normal: 'team_admin', cat: 'group_admin' }
export const ADMIN_ROLES = ['team_admin', 'group_admin']

/** Admin of one specific board. */
export const isModeAdmin = (actor, mode) => has(actor, MODE_ADMIN_ROLE[mode])

/** Holds either admin role — for rights that belong to no single board. */
export const isAnyAdmin = (actor) => ADMIN_ROLES.some((r) => has(actor, r))

/**
 * Owner, or the admin OF THIS MODE.
 *
 * Every board rule takes this rather than a mode-blind "is an admin", and that is the whole
 * point of the split: a Team admin has no write on the Group board and vice versa. The
 * previous mode-blind isAdminish() sat inside canCreate/canMutate/canWriteBoard, so any
 * admin could edit either board — passing ctx.mode through here is what closes that.
 */
export const isAdminFor = (actor, mode) => isOwner(actor) || isModeAdmin(actor, mode)

/**
 * Owner or ANY admin. Only for configuration that is not tied to one board — the people
 * list, job types, who may send the schedule email. Never use this to gate a board write;
 * that is what isAdminFor() is for.
 */
export const isAdminish = (actor) => isOwner(actor) || isAnyAdmin(actor)

/** PMs whose scope this actor acts within: themselves if a PM, plus every PM they assist. */
export function pmScope(actor) {
  const ids = new Set()
  if (has(actor, 'pm')) ids.add(actor.id)
  for (const pmId of actor?.assistsPmIds || []) ids.add(pmId)
  return ids
}

/**
 * The four fields the board rules actually read, from either shape an assignment arrives in.
 *
 * The server passes rows straight out of the database (snake_case); the browser passes the
 * API payload repos/board.js builds (camelCase). Normalising here, at the one boundary, is
 * what lets the rules themselves be written once — the alternative was reshaping payloads to
 * suit the gate, which means the wire format is dictated by an internal helper.
 *
 * `mode` falls back to the context because it is a property of the board being looked at,
 * and only the database row happens to carry a copy of it per assignment.
 */
function assignmentFields(a, ctx) {
  return {
    mode: a?.mode ?? ctx?.mode,
    catTeamId: a?.cat_team_id ?? a?.catTeamId ?? null,
    ownerPmId: a?.owner_pm_user_id ?? a?.ownerPmId ?? null,
    staffId: a?.staff_user_id ?? a?.staffId ?? null,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuration rights (§8.1) — where owner and admin diverge
// ─────────────────────────────────────────────────────────────────────────────

// Company-wide, so either admin holds them: a person and a job type exist on both boards,
// and splitting them would leave a Group admin unable to add the staff they schedule.
export const canManageUsers = isAdminish
export const canManageJobTypes = isAdminish

// Each mode's own shape, held by that mode's admin. PM teams are Team mode's structure —
// there are no PMs on the Group board — so they sit with the Team admin.
export const canManagePmStructure = (actor) => isAdminFor(actor, 'normal')
export const canManageNormalSettings = (actor) => isAdminFor(actor, 'normal')
export const canManageCatSettings = (actor) => isAdminFor(actor, 'cat')

// Reserved to owner. These are the actions that change what the whole company sees, that
// hand out a credential, or that expose the record of who did what — so they stay with a
// small number of named people. Switching mode is here rather than with the mode admins on
// purpose: it decides which board the entire company works from, which is not a decision
// belonging to the admin of either one of them.
export const canSwitchMode = isOwner
export const canManageDisplays = isOwner
export const canGrantOwner = isOwner
// Branding is the company's face on every wall TV and printout, and the audit log is the
// record of everyone's actions including the admins' own. Neither is day-to-day work.
export const canManageBranding = isOwner
export const canViewAuditLog = isOwner

/**
 * Kit — vehicles, tools, computers, phones, clothing.
 *
 * A separate role from the two board admins, because looking after the vans is a different
 * job from running a schedule and the people who do it are usually not the same people. It
 * is ADDITIVE: user_roles is keyed (user_id, role), so somebody can hold group_admin and
 * asset_admin at once and this grants nothing on either board.
 *
 * Deliberately not folded into isAdminish. A team admin has no business editing the tool
 * register by virtue of running a board, and the reverse — an asset admin quietly gaining
 * the shared people-and-job-types surface — is the more dangerous direction of the two.
 *
 * Only CHANGING the register needs this. READING it is open to anyone signed in — "which
 * van is ours" is not a secret from the people who drive it, and a register nobody can read
 * is a register nobody keeps up to date — so routes/assets.js gates reads with requireUser
 * and nothing else. There is deliberately no predicate for that: one that can only ever
 * return true is a sentence rather than a rule, and a sentence belongs in prose where it
 * cannot drift out of step with the gate by being left uncalled.
 */
export const canManageAssets = (actor) => isOwner(actor) || has(actor, 'asset_admin')

/**
 * Granting either admin role is owner-only, for the same reason canGrantOwner is: without
 * it, a Team admin could simply grant themselves group_admin and the separation between the
 * two boards would be a suggestion rather than a rule.
 *
 * The asset role is held to the same terms, and so is owner itself, but not through here:
 * repos/users.js keeps all four as OWNER_GRANTED_ROLES and checks that list when a user is
 * created and on every later role change, which is the one place both writes pass through.
 */
export const canGrantAdmin = isOwner

/**
 * Whether the fleet is drawn on the board, and what the company calls it, is company
 * vocabulary like the mode names — owner-only, like them. See migration 013.
 */
export const canManageAssetSettings = isOwner

/**
 * Lists — named boards of people against days, kept beside the schedule rather than on it.
 *
 * Making, renaming and retiring a list is owner-only: a list is a company-wide fixture like
 * a mode name or the register's caption, and every one is another surface the whole company
 * sees. Filling one in is for anyone who fills a board — either admin, or a PM or PMA —
 * because "who is on call Thursday" is decided by the same people, in the same sitting, as
 * who is on which job. Lists carry no mode, so this is the union of the two boards' writers
 * rather than either one's.
 *
 * Reading is open to everyone signed in, the same as the asset register and for the same
 * reason: a list nobody can see is a list nobody keeps up to date. No predicate for it, for
 * the reason given on canManageAssets above — routes/lists.js gates reads with requireUser
 * and the company's own switch. What a reader may see ON a list is a narrower question and
 * does have a rule: it follows the board's visibility, applied in that same file.
 */
export const canManageLists = isOwner
export const canWriteLists = (actor) => isAdminish(actor) || pmScope(actor).size > 0

/**
 * Who has an asset on a given day is decided while that day's board is being filled, by
 * whoever is filling it. So the right is exactly canWriteBoard for the mode in effect on that
 * date: no new role, and no way to hand out a truck on a board you cannot write. The route
 * resolves the mode from the date being written, never from the request. Distinct from
 * canManageAssets above, which is the register: an asset admin who runs no board can say who
 * USUALLY has the van, and a board admin who is not an asset admin can say who has it TODAY.
 */
export const canWriteAssets = canWriteBoard

/**
 * Who may be NAMED the coordinator of a crew — which is a different question from who may
 * change the crews (canManageCatTeams below). Leading one is the Group admin's job, and an
 * owner's, because an owner passes everywhere.
 *
 * Here rather than only in repos/catteams.js because both sides ask it: the server refuses
 * a coordinator who does not qualify, and the picker on Group setup offers only those who
 * do. Those were two hand-kept copies and they had already drifted — the server accepted an
 * owner and the picker did not list one, so a company whose owner leads a crew could not
 * record that without an admin doing it for them. Exactly the drift the note at the top of
 * this file describes, in miniature.
 *
 * Takes anything carrying `roles`, so a user row from the admin list answers it as readily
 * as the session actor does.
 */
export const canCoordinateTeam = (actor) => isOwner(actor) || has(actor, 'group_admin')

/** Group team structure belongs to the Group admin, and to the owner. */
export function canManageCatTeams(actor, catSettings) {
  if (isAdminFor(actor, 'cat')) return true
  // Legacy delegation flag. It existed so the one old mode-blind 'admin' could manage CAT
  // teams without being an owner; group_admin now covers that case directly. Still honoured
  // so a tenant that had switched it on does not silently lose the capability.
  return Boolean(catSettings?.admin_can_manage_teams) && isModeAdmin(actor, 'normal')
}

/**
 * May the actor send the "here is your week" email to the whole company?
 *
 * A named list rather than a role, because this right does not line up with any of them: it
 * is not "can edit the board" (a PM books work all morning without being the person who
 * announces it), and it is not "can configure things" (a dispatcher who runs the morning
 * send has no business in settings).
 *
 * An empty list means UNDECIDED, not "nobody" — otherwise the migration that adds this table
 * silently disables the button for every existing tenant, including the owner who would have
 * to configure it, with no way back except editing the database by hand. Once anybody is
 * named the list is exact, and an owner who leaves themselves off genuinely cannot send;
 * that is recoverable, because managing the list is a settings right, not this one.
 */
export function canNotifyStaff(actor, notifierIds) {
  if (notifierIds?.length) return notifierIds.includes(actor?.id)
  return isAdminish(actor)
}

// ─────────────────────────────────────────────────────────────────────────────
// Board rights
// ─────────────────────────────────────────────────────────────────────────────

/**
 * May the actor create an assignment for this staff member?
 * Normal: any PM/PMA may book anyone — booking outside your own team is a borrow, which is
 * a normal and expected thing to do, not an exception.
 * CAT: the Group admin, who runs the whole surge board. Being a PM or PMA grants nothing
 * here; those are Team-mode roles.
 */
export function canCreate(actor, ctx) {
  if (isAdminFor(actor, ctx.mode)) return true
  if (ctx.mode === 'normal') return pmScope(actor).size > 0
  return false
}

/**
 * May the actor edit or delete this existing assignment?
 *
 * The interesting case is normal mode's second clause: a PM keeps rights over their OWN
 * people even when another PM did the booking. Without it, two PMs who have borrowed each
 * other's crews reach a standstill where neither can fix the board. The UI confirms and the
 * audit log records it (see routes/assignments.js).
 */
export function canMutate(actor, ctx, assignment) {
  if (!assignment) return false

  // Read the mode off the assignment itself, not the context: this is the row being written,
  // and a Team admin must not inherit rights over a Group row just because they are looking
  // at it from a Group-mode board.
  const a = assignmentFields(assignment, ctx)
  if (isAdminFor(actor, a.mode)) return true
  if (a.mode === 'cat') return false

  const mine = pmScope(actor)
  // No PM scope, no rights. The teamStaffIds check below is about WHICH crew, and asking it
  // first would let anyone whose ctx happened to name a staff member through — this file is
  // the gate, so it must not depend on the caller assembling ctx correctly.
  if (mine.size === 0) return false

  if (a.ownerPmId && mine.has(a.ownerPmId)) return true
  return ctx.teamStaffIds?.has(a.staffId) ?? false
}

/** True when the actor is reaching over another PM's booking of their own team member. */
export function isOverride(actor, ctx, assignment) {
  const a = assignmentFields(assignment, ctx)
  if (a.mode !== 'normal') return false
  if (isAdminFor(actor, 'normal')) return false
  const mine = pmScope(actor)
  if (mine.size === 0) return false
  return (
    Boolean(a.ownerPmId) &&
    !mine.has(a.ownerPmId) &&
    (ctx.teamStaffIds?.has(a.staffId) ?? false)
  )
}

/**
 * May the actor write anything at all on this board? Used for board-wide actions (copying a
 * day, widening job columns) that are not about one particular cell.
 */
export function canWriteBoard(actor, ctx) {
  if (isAdminFor(actor, ctx.mode)) return true
  if (ctx.mode === 'normal') return pmScope(actor).size > 0
  return false
}

/** Job columns are a board-shape decision, so anyone who can write the board can widen it. */
export const canManageSlots = canWriteBoard

/**
 * May the actor see the time off of people who are not crew — PMs, PMAs, admins, owners?
 *
 * Their names, titles and phone numbers are everybody's to see: they head every board. Their
 * absences are not the crew's business — an owner's "surgery follow-up" on a field-staff
 * member's calendar — but they are the business of whoever schedules, who needs to know the
 * PM is away before booking the week. So: whoever may write a board, which is the same
 * audience that may record time off at all.
 */
export const canSeeOfficeTimeOff = (actor) =>
  canWriteBoard(actor, { mode: 'normal' }) || canWriteBoard(actor, { mode: 'cat' })

/**
 * May the actor see why somebody is away, the internal note on it, and whether the request
 * for it has gone in (migration 025)?
 *
 * The crew see that a colleague is off, and the public note if one was written. Sick, no-show
 * and "has not put in the paperwork" are between that person and whoever schedules them. The
 * same audience as office time off, and as recording time off at all, but its own rule: the
 * two answer different questions, and one moving should not silently move the other.
 */
export const canSeeTimeOffDetail = (actor) =>
  canWriteBoard(actor, { mode: 'normal' }) || canWriteBoard(actor, { mode: 'cat' })

/** Which PM this booking should be attributed to when the actor creates it. */
export function attributionPmId(actor) {
  if (has(actor, 'pm')) return actor.id
  const assists = actor?.assistsPmIds || []
  return assists.length === 1 ? assists[0] : null
}

// ─────────────────────────────────────────────────────────────────────────────
// Visibility (§8.2 / §8.3)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deliberately broader than the write rules: an admin of either mode SEES both boards, and
 * only writes their own. Someone who runs surge work has every reason to look at what the
 * everyday board is committed to before pulling people onto a storm; hiding it would not
 * protect anything, since both admins can already read the same names under People.
 */
export function visibilityFor(actor, ctx) {
  if (isAdminish(actor)) return 'all'
  if (pmScope(actor).size > 0) return 'all'
  if ((actor?.coordinatesTeamIds || []).length > 0) return 'all'
  return ctx.mode === 'cat'
    ? ctx.catSettings?.staff_visibility || 'team'
    : ctx.tenant?.normal_staff_visibility || 'team'
}

/**
 * Filter a board down to what this actor may see.
 * @param staffIds     every staff id on the board
 * @param peerStaffIds the actor's own team's staff ids (PM team or CAT team)
 */
export function visibleStaffIds(actor, ctx, staffIds, peerStaffIds) {
  const vis = visibilityFor(actor, ctx)
  if (vis === 'all') return new Set(staffIds)
  if (vis === 'own') return new Set([actor.id])
  const set = new Set(peerStaffIds || [])
  set.add(actor.id)
  return set
}

/**
 * The staff the actor's own PM teams contain, read off a board payload.
 *
 * This is the browser's route to the same set the server assembles with SQL in
 * routes/schedule.js's boardContext — it is `ctx.teamStaffIds`, derived from what the client
 * already has rather than from a round trip. It lives here beside the rules that consume it
 * so the two cannot drift apart.
 */
export function teamStaffIdsFromBoard(actor, board) {
  const mine = pmScope(actor)
  const ids = new Set()
  for (const g of board?.groups || []) {
    if (g.kind === 'pm' && mine.has(g.id)) for (const s of g.staffIds) ids.add(s)
  }
  return ids
}
